/**
 * The `assist` function without AWS or Gemini, for `npm run dev`, previews and tests (CLAUDE.md
 * data safety rule 1):
 * - scan: any real photo gives the same sample draft, with one uncertain field (the Parcelle) that
 *   is left out; with a second photo (the back label, owner 2026-10-08) the Parcelle is read
 *   confidently too; a tiny front image (under about 1 KB) is "unreadable", to show that path.
 * - complete: a Domaine containing "Weinbach" gives its country, region, grape, type and last
 *   year to drink, the sample label's Domaine ("Démo") its last year to drink, one containing
 *   "Martin" only unsure values (two producers share the name, so nothing is filled), any other
 *   nothing; with a vintage, "Weinbach" and "Démo" also have sample ratings.
 * - rating: a "Weinbach" wine has two sample ratings, any other none.
 * - a Domaine containing "quota" gets the "free allowance used up" answer, for every operation.
 */
import type { AssistTransport, CompletionFields, ScanFields } from './client';

export const FAKE_SCAN_FIELDS: ScanFields = {
  domaine: { value: 'Domaine de la Démo', confidence: 0.95 },
  cepage: { value: 'Syrah', confidence: 0.9 },
  parcelle: { value: 'Les Grandes Vignes', confidence: 0.45 },
  annee: { value: 2020, confidence: 0.9 },
  pays: { value: 'FR', confidence: 0.85 },
  region: { value: 'Vallée du Rhône', confidence: 0.8 },
  typeDeVin: { value: 'rouge', confidence: 0.8 },
};

const SAMPLE_SOURCES = [{ title: 'example.org', url: 'https://example.org/' }];

/** What the web completion finds for previews, by a word of the Domaine. */
export const FAKE_COMPLETIONS: Record<string, CompletionFields> = {
  weinbach: {
    pays: { value: 'FR', confidence: 0.95 },
    region: { value: 'Alsace', confidence: 0.9 },
    cepage: { value: 'Riesling', confidence: 0.9 },
    typeDeVin: { value: 'blanc', confidence: 0.9 },
    drinkUntilYear: { value: 2035, confidence: 0.8 },
  },
  démo: { drinkUntilYear: { value: 2030, confidence: 0.8 } },
  martin: {
    pays: { value: 'FR', confidence: 0.4 },
    cepage: { value: 'Pinot noir', confidence: 0.3 },
  },
};

/** Sample online ratings for previews: a "Weinbach" wine has two (4.3, shown 4.5), "Démo" one. */
export const FAKE_RATINGS = [
  { site: 'Vivino', score: 4.3, scale: 5, count: 1250 },
  { site: 'Wine-Searcher', score: 93, scale: 100 },
];
const DEMO_RATINGS = [{ site: 'Vivino', score: 3.8, scale: 5, count: 87 }];

export const fakeAssistTransport: AssistTransport = async (request) => {
  const { op, images, domaine } = request as { op?: string; images?: unknown; domaine?: string };
  // A Domaine with "quota" in it shows the "free allowance used up" message (owner, 2026-10-03).
  if (typeof domaine === 'string' && domaine.toLowerCase().includes('quota')) {
    return { ok: false, reason: 'quota', period: 'day' };
  }
  if (op === 'rating' && typeof domaine === 'string') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return domaine.toLowerCase().includes('weinbach')
      ? { ok: true, ratings: FAKE_RATINGS, sources: SAMPLE_SOURCES }
      : { ok: true, ratings: [], sources: [] };
  }
  if (op === 'complete' && typeof domaine === 'string') {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const name = domaine.toLowerCase();
    const key = Object.keys(FAKE_COMPLETIONS).find((word) => name.includes(word));
    const withVintage = (request as { annee?: unknown }).annee !== undefined;
    const ratings = !withVintage
      ? []
      : key === 'weinbach'
        ? FAKE_RATINGS
        : key === 'démo'
          ? DEMO_RATINGS
          : [];
    return { ok: true, fields: key ? FAKE_COMPLETIONS[key] : {}, ratings };
  }
  const photos = Array.isArray(images) ? images : [];
  const [front] = photos;
  if (op !== 'scan' || typeof front !== 'string' || photos.length > 2) {
    return { ok: false, reason: 'invalid_request' };
  }
  // A moment of "reading", as with the real service.
  await new Promise((resolve) => setTimeout(resolve, 300));
  if (front.length < 1400) return { ok: false, reason: 'unreadable' };
  // The back label names the vineyard plot clearly.
  return photos.length === 2
    ? {
        ok: true,
        fields: { ...FAKE_SCAN_FIELDS, parcelle: { value: 'Les Grandes Vignes', confidence: 0.9 } },
      }
    : { ok: true, fields: FAKE_SCAN_FIELDS };
};
